// @migration-in-progress
import { devLog } from '../utils/devLog';
/**
 * Maintenance Requirements Configuration
 * WBC3 for seagoing vessels, Inland Waterways Code for inland vessels
 */

// Import the fitted helpers to check if equipment is actually installed
import { 
  isRadarFitted, 
  isNavtexFitted, 
  isMFFitted, 
  isAISFitted,
  getCategory 
} from './maintenance/helpers/conditions';

export const MAINTENANCE_CATEGORIES = {
  SURVEYS: 'surveys',
  LSA: 'lsa',
  FFE: 'ffe',
  RADIO: 'radio',
  NAVIGATION: 'navigation',
  MACHINERY: 'machinery',
  ELECTRICAL: 'electrical',
  MEDICAL: 'medical',
  SPECIAL_OPS: 'special_ops'
};

// Main function to get maintenance schedule for a vessel
export function getMaintenanceSchedule(vessel) {
  const schedule = {
    vesselId: vessel.id,
    vesselName: vessel.name,
    items: []
  };

  // Check if vessel is inland waterway or seagoing
  const isInlandWaterway = vessel.operatingStatus === 'inland' || vessel.operationType === 'inland';
  
  // For inland waterway vessels, use completely different requirements - NO WBC3
  if (isInlandWaterway) {
    // Check if it's a self-drive hire boat with Class IX(A) path
    const isHireSelfDrive = vessel.type === 'hire_self_drive';
    const useClassIXA = isHireSelfDrive && vessel.inlandRegulatoryPath === 'class_ix_a';
    
    if (useClassIXA) {
      // Self-drive hire boats following Class IX(A) regulations (MGN 469 para 2.4)
      schedule.items.push(...getClassIXARequirements(vessel));
    } else {
      // Standard inland waterway vessels (COP13/MSN 1823) or Hire Boat Code
      schedule.items.push(...getInlandSurveyRequirements(vessel));
      schedule.items.push(...getInlandSafetyEquipment(vessel));
      schedule.items.push(...getInlandFFEMaintenance(vessel)); // Inland-specific fire requirements
      schedule.items.push(...getInlandMachineryMaintenance(vessel)); // Inland machinery standards
      schedule.items.push(...getInlandElectricalMaintenance(vessel)); // Inland electrical standards
      schedule.items.push(...getInlandNavigationMaintenance(vessel));
      // Inland vessels don't need GMDSS radio equipment
      schedule.items.push(...getInlandMedicalRequirements(vessel));
      
      // Add hire boat specific requirements if applicable
      if (isHireSelfDrive && vessel.inlandRegulatoryPath === 'hire_boat_code') {
        // AINA Hire Boat Code 2021 requirements now implemented
        schedule.items.push(...getAINAHireBoatCodeRequirements(vessel));
      }
    }
  } else {
    // SEAGOING vessels follow WBC3
    schedule.items.push(...getSurveyRequirements(vessel));
    schedule.items.push(...getLSAMaintenance(vessel));
    schedule.items.push(...getFFEMaintenance(vessel));
    schedule.items.push(...getMachineryMaintenance(vessel));
    schedule.items.push(...getElectricalMaintenance(vessel));
    schedule.items.push(...getNavigationMaintenance(vessel));
    schedule.items.push(...getRadioMaintenance(vessel));
    schedule.items.push(...getMedicalMaintenance(vessel));
  }
  
  // SPECIAL OPERATIONS - Based on vessel operations (both inland and seagoing)
  if (vessel.operations?.length > 0 || vessel.specialEquipment?.length > 0 || vessel.type === 'tug' || vessel.type === 'pilot') {
    if (isInlandWaterway) {
      schedule.items.push(...getInlandSpecialOpsMaintenance(vessel));
    } else {
      schedule.items.push(...getSpecialOpsMaintenance(vessel));
    }
  }
  
  return schedule;
}

// SURVEY REQUIREMENTS
function getSurveyRequirements(vessel) {
  const surveys = [];
  
  // All vessels require these
  surveys.push({
    category: MAINTENANCE_CATEGORIES.SURVEYS,
    item: 'Annual Examination',
    interval: 12,
    window: 3, // ±3 months
    unit: 'months',
    regulation: 'WBC3 Section 4', // General certification section
    authority: 'Certifying Authority',
    scope: 'General/partial examination'
  });
  
  // Intermediate based on age and area
  if (vessel.age < 10 || getCategory(vessel) <= 2) {
    surveys.push({
      category: MAINTENANCE_CATEGORIES.SURVEYS,
      item: 'Intermediate Examination',
      interval: 30, // 2.5 years
      unit: 'months',
      regulation: 'WBC3 Section 4', // General certification section
      authority: 'Certifying Authority',
      scope: 'Out of water inspection',
      alternative: vessel.age < 15 ? 'In-water survey possible' : null
    });
  }
  
  surveys.push({
    category: MAINTENANCE_CATEGORIES.SURVEYS,
    item: 'Renewal Examination',
    interval: 60, // 5 years
    unit: 'months',
    regulation: 'WBC3 Section 4', // General certification section
    authority: 'Certifying Authority',
    scope: 'Full compliance examination'
  });
  
  return surveys;
}

// LSA MAINTENANCE - CORRECTED REFERENCES
function getLSAMaintenance(vessel) {
  const lsa = [];
  
  devLog('getLSAMaintenance called with vessel:', {
    id: vessel.id,
    name: vessel.name,
    vesselType: vessel.type,
    type: vessel.type,
    category: vessel.category,
    areaCategory: vessel.areaCategory,
    lsaEquipment: vessel.lsaEquipment,
    operatingArea: vessel.operatingArea
  });
  
  // LIFEBUOYS - Consolidated monthly inspection (MANDATORY for all vessels)
  const lifebuoyChecks = [
    'Visual inspection - condition, mountings, markings',
    'Test lights - operation and battery',
    'Check 18m line condition'
  ];
  
  if (getCategory(vessel) <= 2) { // Area 0-2 requires smoke signals
    lifebuoyChecks.push('Check smoke signal expiry dates');
  }
  
  lsa.push({
    category: MAINTENANCE_CATEGORIES.LSA,
    item: 'Lifebuoy Monthly Inspection',
    interval: 1,
    unit: 'months',
    regulation: 'WBC3 14.3',
    requirement: lifebuoyChecks.join('; '),
    checklist: lifebuoyChecks, // Store as array for detailed display if needed
    mandatory: true,
    jobDescription: {
      purpose: 'Ensure lifebuoys are ready for immediate deployment',
      scope: `• Check for cracks or damage
• Test lights work when inverted
• Verify grab lines are secure and not frayed
• Check quick release mechanism operates smoothly
• Inspect reflective tape is intact
• Check smoke signal expiry dates (if fitted)`,
      tools: [],
      completion: 'Record check date and any defects found'
    }
  });
  
  // LIFEJACKETS - MANDATORY for all vessels (100% of persons)
  // Maintenance depends on lifejacket type
  const lifejacketType = vessel.lifejacketType || 'mixed'; // Default to mixed if not specified
  
  if (lifejacketType === 'foam' || lifejacketType === 'mixed') {
    // TEST: Generated data system
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: '🚀 GENERATED DATA TEST - CSV→JS System Working',
      interval: 1,
      unit: 'months',
      regulation: 'CSV→JS v1.0',
      requirement: 'Test verification',
      mandatory: true,
      jobDescription: {
        purpose: 'This item proves the new CSV→JS generation system is working correctly',
        scope: 'Build system verification'
      }
    });

    // Monthly lifejacket inspection including light test
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Lifejacket Monthly Inspection',
      interval: 1,
      unit: 'months',
      regulation: 'WBC3 14.4.3',
      requirement: 'Visual check and light test',
      mandatory: true,
      jobDescription: {
        purpose: 'Verify foam lifejackets are serviceable',
        scope: `• Check fabric and foam for tears or damage
• Test lights work when inverted
• Verify whistles are attached and functional
• Check reflective tape is visible
• Ensure straps and buckles are intact
• Store in dry, accessible location`,
        tools: [],
        completion: 'Record check date and any defects found'
      }
    });
    
    // Annual comprehensive inspection
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Lifejacket Annual Inspection',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 14.4',
      requirement: 'Comprehensive annual inspection',
      mandatory: true,
      jobDescription: {
        purpose: 'Comprehensive annual foam lifejacket inspection',
        scope: `• Check buoyancy foam for deterioration
• Test all buckles and straps for security
• Replace light batteries as needed
• Verify whistles work
• Check reflective tape condition
• Confirm approval markings are legible
• Verify correct quantity onboard (110% of persons)`,
        tools: ['Spare batteries', 'Spare whistles'],
        safety: ['Replace if damaged - do not repair'],
        completion: 'Record date, quantity checked, and defects'
      }
    });
  }
  
  if (lifejacketType === 'inflatable' || lifejacketType === 'mixed') {
    // Inflatable lifejackets require annual professional service
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Inflatable Lifejacket Annual Service',
      interval: 12,
      window: 1,
      unit: 'months',
      regulation: 'WBC3 14.4.9',
      requirement: 'Professional service by authorized service station',
      mandatory: true,
      certificate: 'Service certificate required',
      jobDescription: {
        purpose: 'Arrange professional service for inflatable lifejackets',
        scope: `• Check service due dates on all inflatable jackets
• Count and document serial numbers
• Inspect for obvious damage before sending
• Arrange collection by authorized service station
• Ensure adequate spares during service period
• Verify service certificates on return
• Update maintenance records`,
        tools: [
          'Service station contact details',
          'Transport containers/bags',
          'Service record forms',
          'Spare lifejackets during service period'
        ],
        safety: [
          'Do not attempt to service inflatable lifejackets yourself',
          'Ensure adequate spares while jackets are at service',
          'Use only manufacturer-approved service stations'
        ],
        completion: 'File service certificates and update vessel records'
      }
    });
    
    // Monthly check for inflatable lifejackets
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Inflatable Lifejacket Monthly Check',
      interval: 1,
      unit: 'months',
      regulation: 'WBC3 14.4.3',
      requirement: 'Visual check and indicator inspection',
      mandatory: true,
      jobDescription: {
        purpose: 'Verify inflatable lifejackets are ready for use',
        scope: `• Check inflation status indicators are green
• Inspect CO2 cylinder is secure and not punctured
• Test lights work when inverted
• Verify whistle is attached
• Check manual inflation tube is accessible
• Verify service date is not overdue
• Store in dry, accessible location`,
        tools: [],
        completion: 'Record check date and any issues'
      }
    });
  }
  
  // For mixed fleets, add a clarification note
  if (lifejacketType === 'mixed') {
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Mixed Lifejacket Fleet Management',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 14.4',
      requirement: 'Maintain separate records for foam and inflatable types',
      notes: 'Vessel has both foam and inflatable lifejackets - maintain separately',
      mandatory: false
    });
  }
  
  // Liferafts - MANDATORY for seagoing vessels
  if (getCategory(vessel) <= 6) { // All seagoing categories require liferafts
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Liferaft Service',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 14.2.1.1', // CORRECT - liferaft servicing
      requirement: getCategory(vessel) <= 2 ? 'SOLAS approved station' : 'Manufacturer approved',
      certificate: 'Required at survey',
      mandatory: true
    });
    
    // HRU if fitted
    if (vessel.hru_fitted) {
      lsa.push({
        category: MAINTENANCE_CATEGORIES.LSA,
        item: 'HRU Service/Replacement',
        interval: vessel.hruType === 'disposable' ? 24 : 12,
        unit: 'months',
        regulation: 'WBC3 14.2.3.1', // CORRECT - HRU servicing
        requirement: vessel.hruType === 'disposable' ? 'Replace' : 'Service'
      });
    }
  }
  
  // IMMERSION SUITS - Complex conditional logic based on vessel type and category
  let immersionSuitsRequired = false;
  const category = parseInt(vessel.areaCategory || vessel.category) || 6;
  const vesselType = vessel.type || vessel.type;
  
  // First check if vessel has immersion suits in LSA equipment list
  // Ensure lsaEquipment is an array before calling array methods
  const lsaEquipmentArrayForImmersion = Array.isArray(vessel.lsaEquipment) ? vessel.lsaEquipment : [];
  const hasImmersionSuitsInEquipment = lsaEquipmentArrayForImmersion.includes('immersion_suits');
  
  // Debug logging
  devLog('Immersion Suits Check:', {
    vesselType,
    vesselVesselType: vessel.type,
    vesselType2: vessel.type,
    category,
    lsaEquipment: vessel.lsaEquipment,
    hasImmersionSuitsInEquipment,
    operatingArea: vessel.operatingArea,
    operatingHours: vessel.operatingHours,
    fullVessel: vessel
  });
  
  // Check if inland waterways first (never required)
  if (vessel.operatingArea === 'inland' || String(vessel.category).toLowerCase() === 'iww') {
    immersionSuitsRequired = false;
  }
  // Police boats (seagoing) - always required
  else if (vesselType === 'police') {
    immersionSuitsRequired = true;
  }
  // Pilot boats - conditional
  else if (vesselType === 'pilot') {
    if (category <= 5) {
      immersionSuitsRequired = true; // Cat 0-5 always
    } else if (category === 6 && vessel.operatingHours !== 'daylight_only') {
      immersionSuitsRequired = true; // Cat 6 night ops
    }
  }
  // Safety/Standby boats - required for rescue operations
  else if (vesselType === 'safety_standby') {
    if (category <= 5) {
      immersionSuitsRequired = true; // Required for rescue capability in Cat 0-5
    }
  }
  // Standard vessels Cat 0-5 - only if chosen over insulated lifejackets
  else if (category <= 5) {
    // Check if vessel has opted for immersion suits over insulated lifejackets
    if (vessel.lsaChoice === 'immersion_suits' || vessel.hasImmersionSuits || hasImmersionSuitsInEquipment) {
      immersionSuitsRequired = true;
    }
  }
  // Any vessel that has immersion suits in their LSA equipment
  else if (hasImmersionSuitsInEquipment) {
    immersionSuitsRequired = true;
  }
  
  if (immersionSuitsRequired) {
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Immersion Suit Annual Service',
      interval: 12,
      unit: 'months',
      regulation: vesselType === 'pilot' ? 'WBC3 Section 27.2.3' : 
                  vesselType === 'police' ? 'WBC3 Annex 3' : 'WBC3 Section 14.5',
      requirement: 'Professional inspection - zippers, seals, buoyancy',
      mandatory: true,
      certificate: 'Service certificate required',
      notes: category === 6 && vesselType === 'pilot' ? 
             'Required for Cat 6 pilot boats with night operations' :
             category <= 5 && vesselType === 'pilot' ? 
             'Required for all Cat 0-5 pilot boats' :
             vesselType === 'police' ? 
             'Required for all police boats' :
             vesselType === 'safety_standby' ?
             'Required for safety/standby boats (rescue operations)' :
             'Vessel has opted for immersion suits over insulated lifejackets',
      jobDescription: {
        purpose: 'Annual professional service of immersion suits',
        scope: `• Check all zippers for smooth operation
• Inspect seals around face, wrists, ankles
• Check for tears, punctures, or degradation
• Test buoyancy and insulation integrity
• Verify whistle attachment
• Check reflective tape condition
• Test lights if fitted
• Apply zip lubricant as per manufacturer`,
        tools: ['Zip lubricant', 'Service record forms'],
        safety: ['Professional service recommended for thorough testing'],
        completion: 'File service certificates and update records'
      }
    });
    
    // Monthly visual check
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Immersion Suit Monthly Check',
      interval: 1,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Visual inspection and storage check',
      mandatory: false,
      jobDescription: {
        purpose: 'Ensure immersion suits are ready for immediate use',
        scope: `• Check suits are properly stowed and accessible
• Verify correct sizes available for all crew
• Look for obvious damage or deterioration
• Ensure donning instructions are visible
• Check storage area is dry and ventilated`,
        tools: [],
        completion: 'Record check date and any issues found'
      }
    });
  }
  
  // EPIRB - MANDATORY for Area Category 0-2
  if (getCategory(vessel) <= 2) {
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'EPIRB Self Test',
      interval: 1,
      unit: 'months',
      regulation: 'WBC3 17.5', // CORRECT - EPIRB in radio section
      requirement: 'Self-test only',
      warning: 'Do not activate',
      mandatory: true
    });
    
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'EPIRB Battery',
      interval: 48,
      unit: 'months',
      regulation: 'WBC3 17.5', // CORRECT
      requirement: 'Replace before expiry',
      mandatory: true
    });
    
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'EPIRB Registration',
      interval: 12,
      unit: 'months',
      regulation: 'MGN 665', // CORRECT - EPIRB registration MGN
      requirement: 'Update vessel/owner details',
      mandatory: true
    });
    
    // SART also mandatory for Area 0-2
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'SART Test',
      interval: 1,
      unit: 'months',
      regulation: 'WBC3 14.12', // CORRECT - Search and rescue locating devices
      requirement: 'Self-test only',
      mandatory: true
    });
    
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'SART Battery',
      interval: 60,
      unit: 'months',
      regulation: 'WBC3 14.12', // CORRECT
      requirement: 'Replace before expiry',
      mandatory: true
    });
  }
  
  // Pyrotechnics
  if (getCategory(vessel) <= 3) {
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Pyrotechnic Expiry Check',
      interval: 1,
      unit: 'months',
      regulation: 'WBC3 Table 14.1.2', // CORRECT - requirements table
      requirement: 'Check all flares/smokes',
      replacement: 'Before expiry (3-4 years)',
      mandatory: true
    });
  }
  
  // Recovery equipment - check for specific types first
  // Ensure lsaEquipment is an array before calling array methods
  const lsaEquipmentArray = Array.isArray(vessel.lsaEquipment) ? vessel.lsaEquipment : [];
  const hasSpecificRecoveryEquipment = lsaEquipmentArray.some(eq =>
    ['jason_cradle', 'recovery_basket', 'scramble_nets'].includes(eq)
  );

  // Add specific checks for selected recovery equipment
  if (lsaEquipmentArray.includes('jason_cradle')) {
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: "Jason's Cradle",
      interval: 6,
      unit: 'months',
      regulation: 'WBC3 14.7.1 / LOLER',
      requirement: 'Thorough examination and load test',
      notes: 'Annual certification required if lifting persons',
      mandatory: true,
      jobDescription: `Inspect and test Jason's Cradle man overboard recovery system.

Check:
• Frame structure for cracks, corrosion, or deformation
• Pivot points and hinges for smooth operation
• Quick-release mechanisms function correctly
• Securing pins and clips are present and serviceable
• Instructions/weight limit clearly displayed
• Annual LOLER certification current (if used for lifting)
• Practice deployment with crew (record in drill log)`
    });
  }
  
  if (lsaEquipmentArray.includes('recovery_basket')) {
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Recovery Basket/Davit',
      interval: 6,
      unit: 'months',
      regulation: 'WBC3 14.7.1 / LOLER',
      requirement: 'Inspection and function test',
      notes: 'Annual load test required',
      mandatory: true,
      jobDescription: `Inspect recovery basket and davit system for personnel recovery.

Check:
• Basket structure for damage or corrosion
• Davit arm swings freely and locks securely
• Winch or crane hook engagement
• Load test certificate current (annual requirement)
• SWL clearly marked on equipment
• Crew familiar with deployment procedure
• Recovery strops/slings in good condition`
    });
  }
  
  if (lsaEquipmentArray.includes('scramble_nets')) {
    lsa.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Scramble Nets',
      interval: 3,
      unit: 'months',
      regulation: 'WBC3 14.7.1',
      requirement: 'Inspect for wear and secure attachment',
      mandatory: true,
      jobDescription: `Inspect scramble nets for personnel recovery from water.

Check:
• Net material for cuts, fraying, or UV damage
• All attachment points secure to vessel
• Quick-release mechanism (if fitted) operates correctly
• Net reaches water level when deployed
• No tangling or fouling when stored
• Crew can deploy quickly (practice quarterly)`
    });
  }
  
  // Always add a general MOB equipment check for auxiliary items
  lsa.push({
    category: MAINTENANCE_CATEGORIES.LSA,
    item: hasSpecificRecoveryEquipment ? 'MOB Recovery - Auxiliary Equipment' : 'MOB Recovery Equipment',
    interval: hasSpecificRecoveryEquipment ? 3 : 12,
    unit: 'months',
    regulation: 'WBC3 14.7.1',
    requirement: hasSpecificRecoveryEquipment ? 'Check auxiliary recovery items' : 'Physical demonstration',
    notes: hasSpecificRecoveryEquipment ? 'Covers boat hooks, lines, and other recovery aids' : 'Required at survey, monthly test recommended',
    mandatory: true,
    jobDescription: hasSpecificRecoveryEquipment ? 
`Check auxiliary MOB recovery equipment not covered by specific maintenance cards above.

This check covers:
• Boat hooks and reaching poles - check for damage, secure mounting
• Heaving lines and throw bags - inspect rope condition, practice throwing
• Life rings with lines - ensure quickly deployable, lines not tangled
• MOB marker/dan buoy - check light, flag, and deployment
• Any specialized equipment not listed in settings

💡 TIP: For specialized equipment (hydraulic platforms, custom recovery systems, etc.):
• Use "Add Custom Job" to create specific maintenance cards
• Include manufacturer's maintenance requirements
• Set appropriate inspection intervals` :
`Test your vessel's MOB recovery equipment.

⚠️ TIP: Select your specific recovery equipment in Vessel Settings > Safety Equipment > LSA Equipment to get automatic maintenance checks for:
• Jason's Cradle (6-monthly inspection)
• Recovery Basket/Davit (6-monthly with LOLER requirements)
• Scramble Nets (3-monthly inspection)

This generic check covers:
• Boat hooks and reaching poles
• Heaving lines and throw bags
• Life rings with lines
• Any recovery equipment you have fitted
• Practice deployment procedures (annual demonstration required)

💡 For specialized equipment not in the list (hydraulic platforms, etc.), use "Add Custom Job" to create specific maintenance cards.`
  });
  
  return lsa;
}

// FFE MAINTENANCE - WBC3 FOR SEAGOING VESSELS ONLY
function getFFEMaintenance(vessel) {
  const ffe = [];
  
  // Portable extinguishers - MANDATORY for all vessels
  ffe.push({
    id: 'ffe_ext_visual_monthly',
    category: MAINTENANCE_CATEGORIES.FFE,
    item: 'Fire Extinguisher Visual',
    interval: 1,
    unit: 'months',
    frequencyDays: 30,
    regulation: 'WBC3 16.3.2.3',
    requirement: 'Crew check, log in book',
    scope: 'Pressure, damage, access',
    mandatory: true,
    trackingMode: 'simple',  // Visual check - no equipment write-back needed
    equipmentLinks: ['extinguisher'],  // Links to equipment type
    equipmentFilter: { category: 'FFE', types: ['fire_extinguisher'] },
    linkedEquipmentIds: [],
    jobDescription: {
      purpose: 'Ensure fire extinguishers are ready for immediate use',
      scope: `• Verify in designated location with clear access
• Check pressure gauge is in green zone
• Inspect for physical damage or corrosion
• Verify safety pin and tamper seal intact
• Check hose/nozzle for blockages
• Turn powder extinguishers upside down to prevent caking
• Verify last service date within 12 months
• Ensure instructions are legible`,
      tools: [
        'Inspection checklist or logbook',
        'Torch for checking in dark spaces',
        'Scales for weighing CO2 extinguishers',
        'Marker pen for inspection tags',
        'Spare tamper seals'
      ],
      safety: [
        'Do not discharge extinguishers for testing',
        'Handle CO2 horns carefully - can cause cold burns',
        'Report missing or discharged extinguishers immediately',
        'Do not remove extinguishers without replacement',
        'If pressure is low, replace immediately'
      ],
      completion: 'Sign and date inspection tag on each extinguisher, record in fire equipment log with any deficiencies noted'
    }
  });
  
  ffe.push({
    id: 'ffe_ext_annual',
    category: MAINTENANCE_CATEGORIES.FFE,
    item: 'Fire Extinguisher Service',
    interval: 12,
    unit: 'months',
    frequencyDays: 365,
    regulation: 'WBC3 16.3.1.8',
    requirement: 'Competent person',
    standard: 'BS EN 3',
    mandatory: true,
    trackingMode: 'individual',  // Each extinguisher tracked separately
    equipmentFilter: { category: 'FFE', types: ['fire_extinguisher'] },
    linkedEquipmentIds: [],
    jobDescription: {
      purpose: 'Annual professional service to ensure reliability',
      scope: `• External inspection for damage or corrosion
• Check pressure gauge reading (if fitted)
• Weigh CO2 extinguishers (must be within 10% of stated weight)
• Invert powder extinguishers to prevent compaction
• Check hose condition and nozzle operation
• Verify safety pin and tamper seal intact
• Update service label with date and signature
• Replace any damaged or expired units`,
      tools: [
        'Weighing scales for CO2 units',
        'Pressure testing equipment (if required)',
        'Service tags and seals'
      ],
      safety: [
        'Use qualified service technician',
        'Do not discharge during service',
        'Replace immediately if condemned'
      ],
      completion: 'Issue service certificate and update fire equipment register'
    }
  });

  ffe.push({
    id: 'ffe_ext_5y',
    category: MAINTENANCE_CATEGORIES.FFE,
    item: 'Fire Extinguisher — 5-Year Extended Service',
    interval: 60,
    unit: 'months',
    frequencyDays: 1826,  // 5 years
    regulation: 'BS EN 3',
    requirement: 'Internal inspection and refill',
    standard: 'BS EN 3',
    mandatory: true,
    trackingMode: 'individual',  // Each extinguisher tracked separately
    equipmentFilter: { category: 'FFE', types: ['fire_extinguisher'] },
    linkedEquipmentIds: [],
    jobDescription: {
      purpose: 'Extended service requiring internal inspection and agent replacement',
      scope: `• Discharge and depressurize extinguisher
• Disassemble and internal inspection
• Replace fire suppression agent
• Pressure test cylinder
• Reassemble and repressurize
• Update service label with 5-year service date
• Issue new service certificate`,
      tools: [
        'Approved discharge facility',
        'Pressure testing equipment',
        'Replacement agent (correct type)',
        '5-year service tags'
      ],
      safety: [
        'Must use certified service technician',
        'Follow manufacturer specifications',
        'Dispose of agent safely',
        'Replace unit if failed pressure test'
      ],
      completion: 'Issue 5-year service certificate and update equipment register'
    }
  });

  // Fire buckets - MANDATORY for all vessels
  ffe.push({
    category: MAINTENANCE_CATEGORIES.FFE,
    item: 'Fire Buckets Check',
    interval: 1,
    unit: 'months',
    regulation: 'WBC3 16.6',
    requirement: 'Check filled with sand/water',
    mandatory: true,
    equipmentLinks: ['bucket'],  // Links to fire buckets
    jobDescription: {
      purpose: 'Ensure fire buckets are ready for immediate use',
      scope: `• Check buckets are filled with sand or water as specified
• Verify handles are secure and not corroded
• Ensure buckets are clearly marked "FIRE"
• Check mounting brackets or stands are secure
• Replace water if contaminated or frozen
• Top up sand if level has dropped
• Verify lanyards are attached and in good condition`,
      completion: 'Note any buckets requiring refill or replacement'
    }
  });
  
  // Fixed systems - MANDATORY if engine below deck (but RIBs don't have engines below deck!)
  if (vessel.enginePosition === 'below_deck' && vessel.type !== 'rib') {
    const systemType = vessel.fixedFireSystemType || 'CO2';
    
    ffe.push({
      category: MAINTENANCE_CATEGORIES.FFE,
      item: `${systemType} System Test`,
      interval: 12,
      unit: 'months',
      regulation: systemType === 'CO2' ? 'MGN 354' : 'MGN 657', // CORRECT - CO2 and aerosol MGNs
      requirement: 'Full function test',
      scope: 'Alarms, valves, controls',
      mandatory: true,
      jobDescription: {
        purpose: `Verify ${systemType} suppression system operates correctly`,
        scope: `• Test all alarm circuits and warning lights
• Check manual release cables move freely
• Verify automatic release mechanism (if fitted)
• Test engine shut-down interlocks
• Check ventilation dampers close on activation
• Inspect all nozzles for blockage
• Verify system pressure (if applicable)
• Test remote monitoring panel
• Check all valves operate smoothly`,
        safety: [
          'Do NOT discharge system',
          'Isolate system before testing',
          'Ensure backup fire protection available'
        ],
        completion: 'Log test results and any defects requiring attention'
      }
    });
    
    if (systemType === 'CO2') {
      ffe.push({
        category: MAINTENANCE_CATEGORIES.FFE,
        item: 'CO2 Cylinder Weight',
        interval: 12,
        unit: 'months',
        regulation: 'MGN 354', // CORRECT
        requirement: 'Check contents ±10%',
        mandatory: true,
        jobDescription: {
          purpose: 'Verify CO2 cylinders contain sufficient agent',
          scope: `• Weigh each cylinder using certified scales
• Compare to stamped gross weight on cylinder
• Calculate if weight is within 10% tolerance
• Check cylinder date stamp for hydrostatic due date
• Inspect cylinder for damage or corrosion
• Verify cylinder is properly secured
• Check flexible discharge hoses for damage`,
          tools: [
            'Certified weighing scales',
            'Cylinder weight chart'
          ],
          completion: 'Record actual vs required weights, flag any requiring refill'
        }
      });
      
      ffe.push({
        category: MAINTENANCE_CATEGORIES.FFE,
        item: 'CO2 Cylinder Hydrostatic',
        interval: 120,
        unit: 'months',
        regulation: 'MGN 354', // CORRECT
        requirement: 'Pressure test',
        mandatory: true
      });
    }
  }
  
  // Detection Systems - Consolidated monthly test
  const detectionChecks = ['Test all fire detectors'];
  let detectionRegulations = 'WBC3 15.6.1.1';
  
  if (vessel.hasAccommodation && vessel.hasGasAppliances) {
    detectionChecks.push('Test CO detector (test button)');
    detectionRegulations += ', 15.6.3';
  }
  
  if (vessel.hasLPGSystem) {
    detectionChecks.push('Gas detector bump test');
    detectionRegulations += ', 15.6.4';
  }
  
  ffe.push({
    id: 'ffe_detectors_monthly',
    category: MAINTENANCE_CATEGORIES.FFE,
    item: 'Detection Systems Monthly Test',
    interval: 1,
    unit: 'months',
    frequencyDays: 30,
    regulation: detectionRegulations,
    requirement: detectionChecks.join('; '),
    checklist: detectionChecks,
    mandatory: true,
    trackingMode: 'group',  // Test all together, but can specify individuals
    equipmentFilter: { category: 'FFE', types: ['smoke_detector', 'heat_detector', 'co_detector', 'gas_detector'] },
    linkedEquipmentIds: [],
    notes: vessel.hasLPGSystem ? 'Gas detector requires annual calibration with certified gas' : undefined,
    jobDescription: {
      purpose: 'Verify detection systems provide early warning',
      scope: `• Test each fire detector using test button
• Verify alarms sound at all required locations
• Check control panel for fault indications
• Clean detector heads of dust/debris
• Test CO detectors if fitted (should read 0 ppm)
• Bump test gas detectors with butane (do not ignite)
• Check automatic gas shut-off operation if fitted
• Test battery backup if installed`,
      tools: [
        'Smoke test aerosol (approved type)',
        'Butane lighter for gas detector bump test',
        'Soft brush or compressed air for cleaning',
        'Multimeter for checking power supply',
        'Calibration gas cylinder (for annual calibration only)'
      ],
      safety: [
        'Never use open flame to test detectors',
        'Ensure ventilation when bump testing gas detectors',
        'Notify crew before testing to avoid unnecessary alarm',
        'Do not disable systems without establishing fire watch'
      ],
      completion: 'Log test date, detector locations tested, any faults found, and corrective actions in maintenance record'
    }
  });

  ffe.push({
    id: 'ffe_detectors_annual',
    category: MAINTENANCE_CATEGORIES.FFE,
    item: 'Detection Systems Annual Service',
    interval: 12,
    unit: 'months',
    frequencyDays: 365,
    regulation: 'WBC3 15.6',
    requirement: 'Full service and calibration',
    mandatory: true,
    trackingMode: 'group',  // Service all together
    equipmentFilter: { category: 'FFE', types: ['smoke_detector', 'heat_detector', 'co_detector', 'gas_detector'] },
    linkedEquipmentIds: [],
    jobDescription: {
      purpose: 'Annual maintenance and calibration of detection systems',
      scope: `• Clean all detector heads thoroughly
• Replace batteries in standalone units
• Calibrate CO and gas detectors with certified gas
• Test all alarm circuits and sounders
• Verify control panel operation
• Check wiring and connections for corrosion
• Update service labels
• Replace units exceeding manufacturer lifespan`,
      tools: [
        'Calibration gas cylinders',
        'Replacement batteries',
        'Soft brushes or compressed air',
        'Multimeter',
        'Service tags'
      ],
      safety: [
        'Use certified calibration gas only',
        'Ensure adequate ventilation',
        'Notify crew before testing alarms',
        'Replace end-of-life detectors immediately'
      ],
      completion: 'Issue service certificate and update detection systems register'
    }
  });

  // Fire blankets
  if (vessel.hasGalley || vessel.fireBlanketsFitted) {
    ffe.push({
      category: MAINTENANCE_CATEGORIES.FFE,
      item: 'Fire Blankets Inspection',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 16.5', // CORRECT - fire blankets
      requirement: 'Check condition and accessibility',
      jobDescription: {
        purpose: 'Ensure fire blankets can be deployed quickly in an emergency',
        scope: `• Check blanket is properly mounted and clearly marked
• Verify quick-release tabs are intact and accessible
• Inspect for damage, contamination or deterioration
• Ensure instructions are visible and legible
• Check wall mounting bracket is secure
• Verify no obstructions blocking access
• Test that blanket can be pulled out smoothly`,
        completion: 'Record condition and any issues found'
      }
    });
  }
  
  // Fire axe
  if (vessel.length >= 15 || vessel.fireAxeFitted) {
    ffe.push({
      category: MAINTENANCE_CATEGORIES.FFE,
      item: 'Fire Axe Condition',
      interval: 12,
      unit: 'months',
      regulation: 'General', // Fire axe not specifically referenced
      requirement: 'Check condition and mounting',
      jobDescription: {
        purpose: 'Ensure fire axe is ready for emergency use',
        scope: `• Check axe head for rust or damage
• Verify handle is secure with no cracks or splinters
• Test edge sharpness (should be reasonably sharp)
• Check mounting bracket is secure
• Verify axe can be removed quickly
• Ensure location signage is visible
• Clean and oil metal parts if needed`,
        completion: 'Note condition and any maintenance performed'
      }
    });
  }
  
  return ffe;
}

// MACHINERY MAINTENANCE - WBC3 FOR SEAGOING VESSELS
function getMachineryMaintenance(vessel) {
  const machinery = [];
  const engineType = vessel.engineType || 'diesel'; // 'diesel', 'petrol', 'outboard'
  
  // Engine servicing - always per manufacturer
  machinery.push({
    category: MAINTENANCE_CATEGORIES.MACHINERY,
    item: 'Engine Oil Change',
    interval: engineType === 'diesel' ? 250 : 100,
    unit: 'hours',
    regulation: 'Manufacturer',
    requirement: 'Oil and filter change',
    alternative: '12 months if low hours'
  });
  
  // Fuel filters - only reference WBC3 for diesel inboards
  if (engineType !== 'outboard') {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Fuel Filters',
      interval: engineType === 'diesel' ? 500 : 250,
      unit: 'hours',
      regulation: 'WBC3 8.10',
      requirement: 'Primary and secondary'
    });
  } else {
    // Outboards have simpler fuel systems
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Fuel Filter',
      interval: 100,
      unit: 'hours',
      regulation: 'Manufacturer',
      requirement: 'Replace inline filter',
      alternative: 'Annual'
    });
  }
  
  // Fuel system
  machinery.push({
    category: MAINTENANCE_CATEGORIES.MACHINERY,
    item: 'Fuel System Pressure Test',
    interval: 12,
    unit: 'months',
    regulation: 'WBC3 8.11', // CORRECT - fuel tanks and systems
    requirement: 'Check for leaks'
  });
  
  // Gearbox
  if (vessel.engineType !== 'outboard') {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Gearbox Oil',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 8.2', // CORRECT - gearboxes
      requirement: 'Check monthly, change annually'
    });
  }
  
  // Stern gear
  if (vessel.propulsionType === 'shaft') {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Stern Gland Monthly Inspection',
      interval: 1,
      unit: 'months',
      regulation: 'Good practice / WBC3 8.3',
      requirement: 'Check adjustment, packing condition, leak rate',
      notes: 'Separate from pre-voyage visual check'
    });
    
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Shaft Alignment',
      interval: 60,
      unit: 'months',
      regulation: 'WBC3 8.3', // CORRECT
      requirement: 'Or if vibration noted'
    });
  }
  
  // Steering
  machinery.push({
    category: MAINTENANCE_CATEGORIES.MACHINERY,
    item: 'Steering System Monthly Test',
    interval: 1,
    unit: 'months',
    regulation: 'Good practice / WBC3 10.1',
    requirement: 'Full system test including response times and fluid levels',
    notes: 'Separate from pre-departure lock-to-lock check'
  });
  
  if (vessel.steeringType === 'hydraulic') {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Hydraulic Steering',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 10.2', // CORRECT - hydraulic systems
      requirement: 'Check fluid, hoses, leaks'
    });
  }
  
  // Emergency steering
  if (vessel.hasEmergencySteering) {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Emergency Steering Test',
      interval: 3,
      unit: 'months',
      regulation: 'WBC3 10.3', // CORRECT - emergency steering
      requirement: 'Full operation test'
    });
  }
  
  // Bilge Systems - Consolidated monthly test
  machinery.push({
    category: MAINTENANCE_CATEGORIES.MACHINERY,
    item: 'Bilge Systems Monthly Test',
    interval: 1,
    unit: 'months',
    regulation: 'WBC3 11.1-11.2',
    requirement: 'Run all pumps; Test alarms/sensors/floats',
    checklist: [
      'Run all bilge pumps',
      'Test bilge alarm sensors and floats',
      'Check pump suctions clear',
      'Verify automatic operation'
    ],
    jobDescription: {
      purpose: 'Ensure bilge systems can remove water effectively',
      scope: `• Operate manual pumps through several strokes
• Check for smooth operation without binding
• Test electric pumps for 30 seconds minimum
• Verify strong water flow at discharge
• Test float switches trigger pumps and alarms
• Clean pump intake screens and strainers
• Check bilge suctions are clear
• Verify alarm sounds in wheelhouse
• Test emergency bilge suction if fitted`,
      tools: [
        'Bucket of water for pump testing',
        'Multimeter or clamp meter for current checks',
        'Wire brush for cleaning strainers',
        'Lubricating oil/grease as specified',
        'Torch for bilge inspection'
      ],
      safety: [
        'Ensure adequate ventilation in bilge spaces',
        'Check for fuel/oil before pumping overboard',
        'Do not pump oily water overboard - use shore facility',
        'Beware of moving parts when testing',
        'Isolate power when cleaning electric pumps'
      ],
      completion: 'Record pump operation times, alarm test results, and any maintenance performed in the engine room log'
    }
  });
  
  // OPTIONAL DECK MACHINERY
  if (vessel.optionalEquipment?.includes('bow_thruster')) {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Bow Thruster Service',
      interval: 12,
      unit: 'months',
      regulation: 'Manufacturer',
      requirement: 'Oil change and seal inspection'
    });
  }
  
  if (vessel.optionalEquipment?.includes('windlass')) {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Anchor Windlass Service',
      interval: 6,
      unit: 'months',
      regulation: 'Manufacturer',
      requirement: 'Grease, test clutch and brake'
    });
  }
  
  if (vessel.optionalEquipment?.includes('capstan')) {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Capstan Maintenance',
      interval: 6,
      unit: 'months',
      regulation: 'Manufacturer',
      requirement: 'Lubrication and load test'
    });
  }
  
  if (vessel.optionalEquipment?.includes('mooring_winches')) {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Mooring Winch Service', 
      interval: 6,
      unit: 'months',
      regulation: 'Manufacturer',
      requirement: 'Brake test and lubrication'
    });
  }
  
  // OPTIONAL AUXILIARY SYSTEMS
  if (vessel.optionalEquipment?.includes('generator')) {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Generator Service',
      interval: 250,
      unit: 'hours',
      regulation: 'Manufacturer',
      requirement: 'Oil, filters, coolant',
      alternative: '12 months if low hours'
    });
  }
  
  if (vessel.optionalEquipment?.includes('watermaker')) {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Watermaker Flush',
      interval: 7,
      unit: 'days',
      regulation: 'Manufacturer',
      requirement: 'Fresh water flush if not used'
    });
    
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Watermaker Filters',
      interval: 6,
      unit: 'months',
      regulation: 'Manufacturer',
      requirement: 'Pre-filters and membrane check'
    });
  }
  
  if (vessel.optionalEquipment?.includes('sewage_treatment')) {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Sewage Treatment Plant',
      interval: 3,
      unit: 'months',
      regulation: 'MARPOL Annex IV',
      requirement: 'Chlorine tablets and pump check'
    });
  }
  
  if (vessel.optionalEquipment?.includes('oily_water_separator')) {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Oily Water Separator',
      interval: 3,
      unit: 'months',
      regulation: 'MARPOL Annex I',
      requirement: 'Clean filters, test 15ppm alarm'
    });
  }
  
  if (vessel.optionalEquipment?.includes('air_compressor')) {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Air Compressor',
      interval: 6,
      unit: 'months',
      regulation: 'PSSR 2000',
      requirement: 'Drain tank, check safety valve'
    });
  }
  
  return machinery;
}

// ELECTRICAL MAINTENANCE - WBC3 FOR SEAGOING VESSELS ONLY
function getElectricalMaintenance(vessel) {
  const electrical = [];
  
  electrical.push({
    category: MAINTENANCE_CATEGORIES.ELECTRICAL,
    item: 'Battery Check',
    interval: 1,
    unit: 'months',
    regulation: 'WBC3 9.1',
    requirement: 'Levels, terminals, charge',
    jobDescription: {
      purpose: 'Ensure batteries maintain proper charge and condition',
      scope: `• Check electrolyte levels in each cell (add distilled water if needed)
• Clean terminals and apply protective coating
• Check specific gravity with hydrometer (1.265-1.285)
• Verify charging voltage (13.8-14.4V)
• Inspect for cracks, swelling or damage
• Check security of battery hold-downs
• Test emergency battery disconnect if fitted`,
      tools: [
        'Hydrometer',
        'Digital multimeter',
        'Terminal cleaning brush',
        'Protective gloves and eyewear'
      ],
      safety: [
        'Wear protective equipment',
        'No smoking or naked flames',
        'Ensure adequate ventilation'
      ],
      completion: 'Record voltage, specific gravity, and any water added'
    }
  });
  
  electrical.push({
    category: MAINTENANCE_CATEGORIES.ELECTRICAL,
    item: 'Battery Load Test',
    interval: 12,
    unit: 'months',
    regulation: 'WBC3 9.1',
    requirement: 'Capacity test'
  });
  
  electrical.push({
    category: MAINTENANCE_CATEGORIES.ELECTRICAL,
    item: 'Insulation Test',
    interval: 12,
    unit: 'months',
    regulation: 'WBC3 9.2',
    requirement: 'Megger test >1MΩ'
  });
  
  if (vessel.hasShorepower) {
    electrical.push({
      category: MAINTENANCE_CATEGORIES.ELECTRICAL,
      item: 'Shore Power RCD',
      interval: 'Before each use',
      regulation: 'WBC3 9.3',
      requirement: 'Test trip function'
    });
  }
  
  electrical.push({
    category: MAINTENANCE_CATEGORIES.ELECTRICAL,
    item: 'Emergency Lighting',
    interval: 1,
    unit: 'months',
    regulation: 'WBC3 9.4',
    requirement: 'Test operation',
    jobDescription: {
      purpose: 'Ensure emergency lighting activates during power failure',
      scope: `• Switch off main lighting circuit breaker
• Verify all emergency lights illuminate
• Check brightness is adequate for safe movement
• Test duration (should last minimum 30 minutes)
• Inspect exit signs are clearly visible
• Check emergency torch locations and charge
• Test wheelhouse emergency lighting
• Restore main power and verify normal operation`,
      completion: 'Record any lights that failed to operate'
    }
  });
  
  return electrical;
}

// NAVIGATION MAINTENANCE
function getNavigationMaintenance(vessel) {
  const nav = [];
  
  // Magnetic compass - MANDATORY for all vessels
  nav.push({
    category: MAINTENANCE_CATEGORIES.NAVIGATION,
    item: 'Compass Adjustment',
    interval: 24,
    unit: 'months',
    regulation: 'WBC3 19.1', // CORRECT - magnetic compass
    requirement: 'If deviation >5°',
    condition: 'Or after structural changes',
    mandatory: true
  });
  
  // Navigation Lights & Signals - Consolidated monthly test
  const navChecks = [
    'Test all navigation lights, switches, sectors, dimmer',
    'Test whistle/horn/bell'
  ];
  
  nav.push({
    category: MAINTENANCE_CATEGORIES.NAVIGATION,
    item: 'Navigation Lights & Signals Monthly Test',
    interval: 1,
    unit: 'months',
    regulation: 'WBC3 18.1-18.2',
    requirement: navChecks.join('; '),
    checklist: navChecks,
    notes: 'Separate from pre-departure checks',
    mandatory: true,
    jobDescription: {
      purpose: 'Verify navigation lights and sound signals function correctly',
      scope: `• Test port (red) and starboard (green) lights
• Test stern light (white)
• Test masthead/steaming light if fitted
• Check anchor light operation
• Test all-round lights as applicable
• Verify light sectors and visibility angles
• Test dimmer control if fitted
• Sound whistle/horn for clear tone
• Ring bell if required for vessel length`,
      tools: [
        'None required'
      ],
      completion: 'Note any defective lights or weak sound signals'
    }
  });
  
  // Day shapes - MANDATORY for all vessels
  nav.push({
    category: MAINTENANCE_CATEGORIES.NAVIGATION,
    item: 'Day Shapes Condition',
    interval: 12,
    unit: 'months',
    regulation: 'COLREGs', // Day shapes per collision regulations
    requirement: 'Check condition and completeness',
    mandatory: true
  });
  
  // Navigation light spares - MANDATORY
  nav.push({
    category: MAINTENANCE_CATEGORIES.NAVIGATION,
    item: 'Navigation Light Spares',
    interval: 3,
    unit: 'months',
    regulation: 'WBC3 18.1', // CORRECT
    requirement: 'Check spare bulbs available',
    notes: 'Verify before each departure',
    mandatory: true
  });
  
  // Signalling Equipment - Consolidated monthly test if applicable
  const signallingChecks = [];
  let signallingRequired = false;
  
  if (getCategory(vessel) <= 3 || getCategory(vessel) === 5) {
    signallingChecks.push('Test signalling lamp operation');
    signallingRequired = true;
  }

  if (getCategory(vessel) <= 2) {
    signallingChecks.push('Test searchlight operation and movement');
    signallingRequired = true;
  }
  
  if (signallingRequired) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'Signalling Equipment Monthly Test',
      interval: 1,
      unit: 'months',
      regulation: 'WBC3 14.12.1',
      requirement: signallingChecks.join('; '),
      checklist: signallingChecks,
      mandatory: true
    });
  }
  
  // MANDATORY navigation equipment based on vessel configuration
  
  // GPS - MANDATORY for Area 0-4
  if (getCategory(vessel) <= 4) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'GPS Accuracy Check',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 19.3', // CORRECT - GPS
      requirement: getCategory(vessel) <= 2 ? 'Verify accuracy of 2 GPS units' : 'Verify accuracy and datum',
      mandatory: true
    });
  }

  // Echo Sounder - MANDATORY for Area 0-4
  if (getCategory(vessel) <= 4) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'Echo Sounder Calibration',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 19.6', // CORRECT - echo sounder
      requirement: 'Calibration check',
      mandatory: true
    });
  }
  
  // Radar - Only if actually fitted (required for Area 0-2 but only maintain if installed)
  if (isRadarFitted(vessel)) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'Radar Performance Check',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 19.2', // CORRECT - radar
      requirement: 'Performance check and magnetron test',
      mandatory: true
    });
  }
  
  // AIS - Only if actually fitted (required for large vessels but only maintain if installed)
  if (isAISFitted(vessel)) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'AIS Data Verification',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 19.7', // CORRECT - AIS
      requirement: 'Verify MMSI and vessel data',
      mandatory: true
    });
  }
  
  // NAVTEX - Only if actually fitted (required for Area 0-2 but only maintain if installed)
  if (isNavtexFitted(vessel)) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'NAVTEX Receiver Test',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 17.5', // NAVTEX in radio section
      requirement: 'Reception test and area selection',
      mandatory: true
    });
  }
  
  // OPTIONAL navigation equipment - based on what's fitted
  // Only truly optional equipment that isn't mandated by regulations
  
  // Radar for Area 3-6 - REMOVED: already handled by isRadarFitted check above
  // which checks both hasRadar flag AND optionalEquipment array
  
  // GPS for Area 5-6 (optional since not required)
  if (getCategory(vessel) > 4 && vessel.optionalEquipment?.includes('gps')) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'GPS Accuracy',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 19.3',
      requirement: 'Verify accuracy'
    });
  }
  
  // AIS for smaller vessels (optional if not required by tonnage/passengers)
  if (vessel.tonnage < 300 && !vessel.carriesPassengers && vessel.optionalEquipment?.includes('ais')) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'AIS Data',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 19.7',
      requirement: 'Verify MMSI and data'
    });
  }
  
  // Echo sounder for Area 5-6 (optional since not required)
  if (getCategory(vessel) > 4 && vessel.optionalEquipment?.includes('echo_sounder')) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'Echo Sounder',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 19.6',
      requirement: 'Calibration check'
    });
  }
  
  if (vessel.optionalEquipment?.includes('autopilot')) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'Autopilot Calibration',
      interval: 12,
      unit: 'months',
      regulation: 'Manufacturer', // Autopilot per manufacturer
      requirement: 'Check and adjust as needed'
    });
  }
  
  if (vessel.optionalEquipment?.includes('ecdis')) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'ECDIS Chart Updates',
      interval: 3,
      unit: 'months',
      regulation: 'WBC3 19.5', // CORRECT - ECDIS
      requirement: 'Update electronic charts'
    });
  }
  
  // Additional optional navigation equipment
  if (vessel.optionalEquipment?.includes('wind_instruments')) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'Wind Instruments Calibration',
      interval: 12,
      unit: 'months',
      regulation: 'Manufacturer',
      requirement: 'Calibration check'
    });
  }
  
  if (vessel.optionalEquipment?.includes('speed_log')) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'Speed Log Calibration',
      interval: 12,
      unit: 'months',
      regulation: 'Manufacturer',
      requirement: 'Verify accuracy against GPS'
    });
  }
  
  if (vessel.optionalEquipment?.includes('weather_fax')) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'Weather Fax Receiver',
      interval: 12,
      unit: 'months',
      regulation: 'Manufacturer',
      requirement: 'Reception test'
    });
  }
  
  if (vessel.optionalEquipment?.includes('satellite_comms')) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'Satellite Communications',
      interval: 6,
      unit: 'months',
      regulation: 'Service Provider',
      requirement: 'Test call and data transmission'
    });
  }
  
  return nav;
}

// RADIO MAINTENANCE
function getRadioMaintenance(vessel) {
  const radio = [];
  
  // VHF Radio with DSC - MANDATORY for all vessels
  radio.push({
    category: MAINTENANCE_CATEGORIES.RADIO,
    item: 'VHF Radio Test',
    interval: 12,
    unit: 'months',
    regulation: 'WBC3 17.3', // CORRECT - VHF radio
    requirement: 'Performance test',
    mandatory: true
  });
  
  radio.push({
    category: MAINTENANCE_CATEGORIES.RADIO,
    item: 'DSC Test',
    interval: 1,
    unit: 'months',
    regulation: 'WBC3 17.3', // CORRECT - DSC
    requirement: 'Test call to coast',
    mandatory: true
  });
  
  // Portable VHF - MANDATORY for all vessels
  radio.push({
    category: MAINTENANCE_CATEGORIES.RADIO,
    item: 'Portable VHF',
    interval: 1,
    unit: 'months',
    regulation: 'WBC3 17.4', // CORRECT - portable VHF
    requirement: 'Battery check',
    mandatory: true
  });
  
  // MF Radio - Only if actually fitted (required for Area 0-1 but only maintain if installed)
  if (isMFFitted(vessel)) {
    radio.push({
      category: MAINTENANCE_CATEGORIES.RADIO,
      item: 'MF Radio Test',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 17.6', // MF radio for offshore
      requirement: 'Performance test and antenna check',
      mandatory: true
    });
  }
  
  // SART maintenance handled in LSA section for Area 0-2 vessels
  
  return radio;
}

// MEDICAL MAINTENANCE - CORRECTED REFERENCES
function getMedicalMaintenance(vessel) {
  const medical = [];
  
  // First Aid Kit - MANDATORY for all vessels
  medical.push({
    category: MAINTENANCE_CATEGORIES.MEDICAL,
    item: 'First Aid Kit Restock',
    interval: 3,
    unit: 'months',
    regulation: 'MSN 1905', // CORRECT - MSN 1905 covers medical stores
    requirement: 'Check contents, replace expired items',
    mandatory: true
  });
  
  // Medical Chest - Based on area category
  if (getCategory(vessel) <= 2) {
    medical.push({
      category: MAINTENANCE_CATEGORIES.MEDICAL,
      item: 'Medical Chest Expiry Check',
      interval: 6,
      unit: 'months',
      regulation: 'MSN 1905', // CORRECT
      requirement: 'Check all medication expiry dates'
    });
    
    // Controlled drugs if carried
    if (vessel.controlledDrugsCarried) {
      medical.push({
        category: MAINTENANCE_CATEGORIES.MEDICAL,
        item: 'Controlled Drugs Seal/Log',
        interval: 1,
        unit: 'months',
        regulation: 'MSN 1905', // CORRECT
        requirement: 'Check seal intact, update log'
      });
    }
  }
  
  // Oxygen if fitted - Note: Medical oxygen not specifically in WBC3
  if (vessel.medicalOxygenFitted) {
    medical.push({
      category: MAINTENANCE_CATEGORIES.MEDICAL,
      item: 'Oxygen Cylinder Pressure',
      interval: 1,
      unit: 'months',
      regulation: 'Manufacturer', // Changed - not in WBC3 Section 21
      requirement: 'Check pressure gauge'
    });
  }
  
  // Stretcher if fitted - Not specifically in WBC3
  if (vessel.stretcherFitted) {
    medical.push({
      category: MAINTENANCE_CATEGORIES.MEDICAL,
      item: 'Stretcher Condition',
      interval: 12,
      unit: 'months',
      regulation: 'Good practice', // Changed - not in WBC3 Section 21
      requirement: 'Check condition and mountings'
    });
  }
  
  // Resuscitator if fitted
  if (vessel.resuscitatorFitted) {
    medical.push({
      category: MAINTENANCE_CATEGORIES.MEDICAL,
      item: 'Resuscitator Service',
      interval: 12,
      unit: 'months',
      regulation: 'Manufacturer',
      requirement: 'Service per manufacturer'
    });
  }
  
  return medical;
}

// SPECIAL OPERATIONS MAINTENANCE - CORRECTED REFERENCES
function getSpecialOpsMaintenance(vessel) {
  const special = [];
  
  // Lifting equipment
  if (vessel.operations?.includes('survey_operations') || vessel.specialEquipment?.includes('winch_lifting_gear') || vessel.specialEquipment?.includes('crane')) {
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Lifting Equipment Thorough Exam',
      interval: 12,
      unit: 'months',
      regulation: 'LOLER', // CORRECT - Lifting Operations regulations
      requirement: 'Competent person',
      certificate: 'Required'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Wire Rope Inspection',
      interval: 6,
      unit: 'months',
      regulation: 'LOLER', // CORRECT
      requirement: 'Thorough examination'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Proof Load Test',
      interval: 48,
      unit: 'months',
      regulation: 'LOLER', // CORRECT
      requirement: '1.25 x SWL'
    });
  }
  
  // Towing - Comprehensive equipment for tugs and towing vessels
  if (vessel.type === 'tug' || vessel.operations?.includes('towing_operations') || vessel.towing_capable) {
    // Winch systems
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Towing Winch Service',
      interval: 6,
      unit: 'months',
      regulation: 'WBC3 26.3',
      requirement: 'Service, brake test, clutch adjustment'
    });
    
    // Towing wire/rope - critical item
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Main Towing Wire Inspection',
      interval: 1,
      unit: 'months',
      regulation: 'WBC3 26.3/COSWP',
      requirement: 'Full length inspection for broken strands, corrosion, deformation',
      notes: 'Visual inspection before each tow'
    });
    
    // Towing bridle and connections
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Towing Bridle/Pennant',
      interval: 3,
      unit: 'months',
      regulation: 'Industry practice',
      requirement: 'Check shackles, thimbles, splices'
    });
    
    // Emergency towing arrangements
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Emergency Tow Wire/Gob Rope',
      interval: 3,
      unit: 'months',
      regulation: 'WBC3 26.3',
      requirement: 'Inspect full length and securing'
    });
    
    // Towing furniture
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Towing Fairleads/Panama Chocks',
      interval: 6,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Check for wear, sharp edges, cracks'
    });
    
    // Towing pins and brackets
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Towing Pins/Brackets',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 26.3',
      requirement: 'NDT inspection if >5 years old'
    });
    
    // Bollards
    if (vessel.age > 5) {
      special.push({
        category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
        item: 'Towing Bollard NDT',
        interval: 12,
        unit: 'months',
        regulation: 'WBC3 26.3',
        requirement: 'Weld inspection by competent person'
      });
    }
    
    // Emergency release
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Emergency Tow Release',
      interval: 3,
      unit: 'months',
      regulation: 'WBC3 26.3',
      requirement: 'Test operation from all control positions'
    });
    
    // Towing lights
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Towing Light Configuration',
      interval: 1,
      unit: 'months',
      regulation: 'COLREGs',
      requirement: 'Test all towing light combinations',
      notes: 'Check before each tow'
    });
    
    // Additional for larger tugs
    if (vessel.length >= 15) {
      special.push({
        category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
        item: 'Shark Jaws/Towing Pins',
        interval: 6,
        unit: 'months',
        regulation: 'Manufacturer',
        requirement: 'Hydraulic system test and NDT if fitted'
      });
      
      special.push({
        category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
        item: 'Stern Roller',
        interval: 12,
        unit: 'months',
        regulation: 'Manufacturer',
        requirement: 'Bearing inspection and greasing if fitted'
      });
    }
  }
  
  // PILOT BOATS - CORRECTED EQUIPMENT (pilot ladders are on ships, not pilot boats!)
  if (vessel.type === 'pilot' || vessel.operations?.includes('pilot_transfer')) {
    // REGULATORY REQUIRED - Searchlight
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Searchlight Operation Test',
      interval: 6,
      unit: 'months',
      regulation: 'WBC3 14.12',
      requirement: 'Test operation and illumination of pilot boat side',
      mandatory: true,
      classification: 'REGULATORY_REQUIRED'
    });
    
    // REGULATORY REQUIRED - Immersion suits (conditional based on category and operating hours)
    const category = parseInt(vessel.areaCategory || vessel.category) || 6;
    let immersionSuitsRequired = false;
    
    // Cat 0-5: Always required for pilot boats
    if (category <= 5) {
      immersionSuitsRequired = true;
    }
    // Cat 6: Only if operating at night
    else if (category === 6 && vessel.operatingHours !== 'daylight_only') {
      immersionSuitsRequired = true;
    }
    
    if (immersionSuitsRequired) {
      special.push({
        category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
        item: 'Immersion Suit Inspection',
        interval: 12,
        unit: 'months',
        regulation: 'WBC3 Section 27.2.3',
        requirement: 'Check condition, zippers, buoyancy for all persons',
        mandatory: true,
        classification: 'REGULATORY_REQUIRED',
        notes: category === 6 ? 'Required for Cat 6 night operations' : 'Required for pilot boats Cat 0-5'
      });
    }
    
    // INDUSTRY STANDARD - MOB recovery equipment
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'MOB Recovery Equipment Test',
      interval: 6,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Test recovery arrangements for pilot emergencies',
      classification: 'INDUSTRY_STANDARD'
    });
    
    // INDUSTRY STANDARD - Pneumatic fenders
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Pneumatic Fender Pressure Check',
      interval: 3,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Check pressure, valves, securing, chafe protection',
      classification: 'INDUSTRY_STANDARD'
    });
    
    // INDUSTRY STANDARD - Line throwing apparatus (half set)
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Line Throwing Apparatus Check',
      interval: 6,
      unit: 'months',
      regulation: 'WBC3 18.3',
      requirement: 'Check rocket, line condition, launcher',
      classification: 'INDUSTRY_STANDARD'
    });
    
    // OPTIONAL - Compact stretcher (WBC3 23.1 reference)
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Compact Stretcher Inspection',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 23.1',
      requirement: 'Check condition, securing, accessibility',
      classification: 'BEST_PRACTICE'
    });
  }
  
  // Anchoring Equipment - Scaled by vessel size as user noted "6m rib will be different from that of a 24m tug"
  // Small vessels (<10m) - Basic anchor and rope
  if (vessel.length < 10) {
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Anchor & Rope Inspection',
      interval: 3,
      unit: 'months',
      regulation: 'WBC3 20.1',
      requirement: 'Check anchor condition, rope chafe/wear',
      notes: 'Visual inspection for small craft anchors'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Anchor Securing',
      interval: 1,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Check stowage and securing',
      notes: 'Ensure properly secured for sea'
    });
  }
  
  // Medium vessels (10-20m) - Chain/rope combination, possibly manual windlass
  else if (vessel.length >= 10 && vessel.length < 20) {
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Anchor Cable Inspection',
      interval: 6,
      unit: 'months',
      regulation: 'WBC3 20.2',
      requirement: 'Check chain/rope condition, measure wear',
      notes: 'Chain wear <12.5%, rope chafe inspection'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Anchor Securing & Brake',
      interval: 3,
      unit: 'months',
      regulation: 'WBC3 20.1',
      requirement: 'Test securing arrangements and brake if fitted',
      notes: 'Ensure brake holds and releases properly'
    });
    
    // Manual or simple powered windlass
    if (vessel.specialEquipment?.includes('windlass')) {
      special.push({
        category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
        item: 'Windlass Service',
        interval: 12,
        unit: 'months',
        regulation: 'WBC3 20.1',
        requirement: 'Service, grease, test operation',
        notes: 'Check clutch and brake operation'
      });
    }
  }
  
  // Large vessels (20m+) - Full windlass system, chain locker, brake testing
  else if (vessel.length >= 20) {
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Anchor Windlass Service',
      interval: 6,
      unit: 'months',
      regulation: 'WBC3 20.1',
      requirement: 'Full service including motor, gearbox, brake',
      notes: 'Test emergency release and manual operation'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Anchor Chain Calibration',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 20.2',
      requirement: 'Measure wear, end-for-end if required',
      notes: 'Replace if wear exceeds 12.5%'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Windlass Brake Test',
      interval: 12,
      unit: 'months',
      regulation: 'WBC3 20.1/COSWP',
      requirement: 'Load test to 45% of chain breaking strength',
      notes: 'Annual brake holding capacity test'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Chain Locker Inspection',
      interval: 12,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Check drainage, bitter end securing, condition',
      notes: 'Ensure proper drainage and no corrosion buildup'
    });
    
    // For very large vessels or those with heavy ground tackle
    if (vessel.length >= 35) {
      special.push({
        category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
        item: 'Anchor Chain NDT',
        interval: 60,
        unit: 'months',
        regulation: 'Class requirement',
        requirement: 'Non-destructive testing of critical links',
        notes: '5-yearly or at special survey'
      });
      
      special.push({
        category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
        item: 'Windlass Foundation',
        interval: 60,
        unit: 'months',
        regulation: 'Structural integrity',
        requirement: 'Check foundation bolts and deck reinforcement',
        notes: 'Part of special survey inspection'
      });
    }
  }
  
  // Diving Support Equipment - for dive support operations
  if (vessel.operations?.includes('dive_support')) {
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Diving Ladder Inspection',
      interval: 3,
      unit: 'months',
      regulation: 'WBC3 27.4',
      requirement: 'Check condition, securing, extension to 3m below waterline',
      notes: 'Must reach minimum 3m below surface'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Shot Line & Marking System',
      interval: 6,
      unit: 'months',
      regulation: 'Dive regulations',
      requirement: 'Check condition, depth markers, securing',
      notes: 'Essential for diver safety'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Diving Emergency Gas Supply',
      interval: 3,
      unit: 'months',
      regulation: 'HSE diving regulations',
      requirement: 'Check pressure, valve operation, connections',
      notes: 'On-deck emergency air supply'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Diver Recall System',
      interval: 6,
      unit: 'months',
      regulation: 'Commercial diving regulations',
      requirement: 'Test audio/visual recall signals',
      notes: 'Audible and visual emergency recall'
    });
    
    // Enhanced first aid for diving operations
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Enhanced Medical Kit (Diving)',
      interval: 6,
      unit: 'months',
      regulation: 'HSE diving first aid',
      requirement: 'Check contents, oxygen supply, decompression cards',
      notes: 'Diving-specific medical equipment'
    });
  }
  
  // NOTE: Pilot Transfer Equipment moved to consolidated pilot boat section above
  // Pilot ladders are on ships being boarded, NOT on pilot boats!
  
  // Dangerous Goods Equipment - for vessels carrying DG
  if (vessel.operations?.includes('dangerous_goods')) {
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Gas Detection System',
      interval: 6,
      unit: 'months',
      regulation: 'IMDG Code/ADN',
      requirement: 'Calibration and function test',
      notes: 'Required for dangerous goods transport'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Emergency Shower/Eyewash',
      interval: 3,
      unit: 'months',
      regulation: 'IMDG Code',
      requirement: 'Test flow rate and temperature',
      notes: 'Chemical emergency decontamination'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'DG Emergency Equipment',
      interval: 6,
      unit: 'months',
      regulation: 'IMDG Code',
      requirement: 'Check spill kits, PPE, neutralizing agents',
      notes: 'Class-specific emergency response equipment'
    });
  }
  
  // Training and maintenance manuals - all vessels
  special.push({
    category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
    item: 'Training Manual Review',
    interval: 12,
    unit: 'months',
    regulation: 'WBC3 14.8', // CORRECT - Training manual in LSA section
    requirement: 'Review and update content'
  });
  
  special.push({
    category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
    item: 'Maintenance Manual Updates',
    interval: 'As issued',
    regulation: 'WBC3 14.9', // CORRECT - Maintenance manual in LSA section
    requirement: 'Update with manufacturer bulletins'
  });
  
  return special;
}

// INLAND WATERWAYS SPECIAL OPERATIONS MAINTENANCE
function getInlandSpecialOpsMaintenance(vessel) {
  const special = [];
  
  // Lifting equipment - LOLER applies to both inland and seagoing
  if (vessel.operations?.includes('survey_operations') || vessel.specialEquipment?.includes('winch_lifting_gear') || vessel.specialEquipment?.includes('crane')) {
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Lifting Equipment Thorough Exam',
      interval: 12,
      unit: 'months',
      regulation: 'LOLER',
      requirement: 'Competent person',
      certificate: 'Required'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Wire Rope Inspection',
      interval: 6,
      unit: 'months',
      regulation: 'LOLER',
      requirement: 'Thorough examination'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Proof Load Test',
      interval: 48,
      unit: 'months',
      regulation: 'LOLER',
      requirement: '1.25 x SWL'
    });
  }
  
  // Towing - Industry standards for inland vessels
  if (vessel.type === 'tug' || vessel.operations?.includes('towing_operations') || vessel.towing_capable) {
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Towing Winch Service',
      interval: 6,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Service, brake test, clutch adjustment'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Main Towing Wire Inspection',
      interval: 1,
      unit: 'months',
      regulation: 'Industry standard',
      requirement: 'Check for wear, damage, distortion'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Towing Hook Load Test',
      interval: 48,
      unit: 'months',
      regulation: 'Industry standard',
      requirement: 'Test to 1.5 x SWL',
      certificate: 'Required'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Emergency Release Test',
      interval: 3,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Test operation under load'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Towing Bollards/Bitts NDT',
      interval: 60,
      unit: 'months',
      regulation: 'Industry standard',
      requirement: 'Non-destructive testing'
    });
  }
  
  // Pilot operations for inland waters
  if (vessel.type === 'pilot' || vessel.operations?.includes('pilot_transfer')) {
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Pilot Ladder Inspection',
      interval: 1,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Check condition, spreaders, securing'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Transfer Arrangements',
      interval: 6,
      unit: 'months',
      regulation: 'Industry standard',
      requirement: 'Test recovery arrangements for pilot emergencies'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Pneumatic Fender Pressure Check',
      interval: 3,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Check pressure, valves, securing, chafe protection'
    });
  }
  
  // Training and maintenance manuals - inland regulations
  special.push({
    category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
    item: 'Training Manual Review',
    interval: 12,
    unit: 'months',
    regulation: 'COP13 13.4 / MSN 1823 21.3',
    requirement: 'Review and update content'
  });
  
  special.push({
    category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
    item: 'Maintenance Manual Updates',
    interval: 'As issued',
    regulation: 'COP13 13.4 / MSIS29 DSM',
    requirement: 'Update with manufacturer bulletins'
  });
  
  // Anchoring Equipment - Good practice for inland waters
  if (vessel.length < 10) {
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Anchor & Rope Inspection',
      interval: 3,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Check anchor condition, rope chafe/wear',
      notes: 'Visual inspection for small craft anchors'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Anchor Securing',
      interval: 1,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Check stowage and securing'
    });
  } else if (vessel.length >= 10 && vessel.length < 20) {
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Anchor Cable Inspection',
      interval: 6,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Check chain/rope condition, measure wear',
      notes: 'Chain wear <12.5%, rope chafe inspection'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Anchor Securing & Brake',
      interval: 3,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Test securing arrangements and brake if fitted'
    });
    
    if (vessel.specialEquipment?.includes('windlass')) {
      special.push({
        category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
        item: 'Windlass Service',
        interval: 12,
        unit: 'months',
        regulation: 'Manufacturer',
        requirement: 'Service, grease, test operation'
      });
    }
  } else if (vessel.length >= 20) {
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Anchor Windlass Service',
      interval: 6,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Full service including motor, gearbox, brake'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Anchor Chain Calibration',
      interval: 12,
      unit: 'months',
      regulation: 'Industry standard',
      requirement: 'Measure wear, end-for-end if required',
      notes: 'Replace if wear exceeds 12.5%'
    });
  }
  
  // Diving Support Equipment - HSE regulations for inland
  if (vessel.operations?.includes('dive_support')) {
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Diving Ladder Inspection',
      interval: 3,
      unit: 'months',
      regulation: 'HSE diving regulations',
      requirement: 'Check condition, securing, extension',
      notes: 'Must reach minimum 3m below surface'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Shot Line & Marking System',
      interval: 6,
      unit: 'months',
      regulation: 'HSE diving regulations',
      requirement: 'Check condition, depth markers, securing'
    });
    
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Diving Emergency Gas Supply',
      interval: 3,
      unit: 'months',
      regulation: 'HSE diving regulations',
      requirement: 'Check pressure, valve operation, connections'
    });
  }
  
  // Survey operations equipment
  if (vessel.operations?.includes('survey_operations')) {
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Survey Equipment Calibration',
      interval: 12,
      unit: 'months',
      regulation: 'Manufacturer',
      requirement: 'Annual calibration certificate'
    });
  }
  
  // Dangerous goods - ADNR for inland waterways
  if (vessel.operations?.includes('dangerous_goods')) {
    special.push({
      category: MAINTENANCE_CATEGORIES.SPECIAL_OPS,
      item: 'Dangerous Goods Equipment',
      interval: 6,
      unit: 'months',
      regulation: 'ADNR',
      requirement: 'Check spill kits, PPE, neutralizing agents'
    });
  }
  
  return special;
}

// Calculate maintenance due dates
export function calculateMaintenanceDue(vessel, lastMaintenance = {}) {
  const schedule = getMaintenanceSchedule(vessel);
  const dueDates = [];
  
  schedule.items.forEach(item => {
    if (item.interval !== 'Before departure' && 
        item.interval !== 'Before each voyage' && 
        item.interval !== 'Before each use' &&
        item.interval !== 'At survey') {
      
      const last = lastMaintenance[item.item];
      if (last) {
        const nextDue = calculateNextDue(last.date, item.interval, item.unit);
        const daysUntilDue = Math.floor((nextDue - new Date()) / (1000 * 60 * 60 * 24));
        
        dueDates.push({
          ...item,
          lastDone: last.date,
          nextDue: nextDue,
          daysUntilDue: daysUntilDue,
          overdue: daysUntilDue < 0,
          critical: daysUntilDue < 30 && item.regulation.includes('WBC3'),
          status: daysUntilDue < 0 ? 'overdue' : daysUntilDue < 30 ? 'due_soon' : 'compliant'
        });
      } else {
        // Never done - due immediately
        dueDates.push({
          ...item,
          lastDone: null,
          nextDue: new Date(),
          daysUntilDue: 0,
          overdue: true,
          critical: true,
          status: 'overdue'
        });
      }
    }
  });
  
  return dueDates.sort((a, b) => a.daysUntilDue - b.daysUntilDue);
}

function calculateNextDue(lastDate, interval, unit) {
  const next = new Date(lastDate);
  
  switch(unit) {
    case 'months':
      next.setMonth(next.getMonth() + interval);
      break;
    case 'days':
      next.setDate(next.getDate() + interval);
      break;
    case 'hours':
      // For hours-based maintenance, need engine hours tracking
      // This would need integration with engine hour meter
      return null;
    case 'years':
      next.setFullYear(next.getFullYear() + interval);
      break;
    default:
      return null;
  }
  
  return next;
}

// ============================================
// INLAND WATERWAY SPECIFIC REQUIREMENTS
// ============================================

// INLAND WATERWAY SURVEY REQUIREMENTS
function getInlandSurveyRequirements(vessel) {
  const surveys = [];
  
  surveys.push({
    category: MAINTENANCE_CATEGORIES.SURVEYS,
    item: 'Annual Inspection',
    interval: 12,
    window: 3,
    unit: 'months',
    regulation: 'Inland Waterways Code',
    authority: 'Local Authority/MCA',
    scope: 'Safety inspection for inland waters',
    notes: 'Different requirements from seagoing vessels'
  });
  
  surveys.push({
    category: MAINTENANCE_CATEGORIES.SURVEYS,
    item: 'Certificate Renewal',
    interval: 48, // 4 years typical for inland
    unit: 'months',
    regulation: 'Inland Waterways Code',
    authority: 'Certifying Authority',
    scope: 'Full compliance check'
  });
  
  return surveys;
}

// INLAND WATERWAY SAFETY EQUIPMENT
function getInlandSafetyEquipment(vessel) {
  const safety = [];
  
  // Determine inland water category (A, B, C, or D)
  const waterCategory = vessel.waterCategory || vessel.inlandWaterCategory || 'C';
  
  // COP13 Section 13.2 - Lifejackets based on water category
  const lifejacketType = vessel.lifejacketType || 'mixed';
  
  // Category A & B: No lifejackets required (except vulnerable passengers)
  if (waterCategory === 'A' || waterCategory === 'B') {
    // Only if carrying vulnerable passengers - risk assessment based
    if (vessel.carriesVulnerablePassengers) {
      safety.push({
        category: MAINTENANCE_CATEGORIES.LSA,
        item: 'Lifejacket Risk Assessment Review',
        interval: 12,
        unit: 'months',
        regulation: 'COP13 Section 13.2.1',
        requirement: 'Annual review for vulnerable passengers',
        notes: 'Risk assessment determines if lifejackets needed'
      });
    }
  }
  
  // Category C & D: Lifejackets required for all persons
  if (waterCategory === 'C' || waterCategory === 'D') {
    if (lifejacketType === 'foam' || lifejacketType === 'mixed') {
      safety.push({
        category: MAINTENANCE_CATEGORIES.LSA,
        item: 'Lifejacket Monthly Check',
        interval: 1,
        unit: 'months',
        regulation: 'COP13 Section 13.2.2',
        requirement: 'Check condition, straps, markings',
        notes: 'BS/EN 396 (150N) or BS/EN 399 (275N) approved'
      });
    }
    
    if (lifejacketType === 'inflatable' || lifejacketType === 'mixed') {
      safety.push({
        category: MAINTENANCE_CATEGORIES.LSA,
        item: 'Inflatable Lifejacket Service',
        interval: 12,
        unit: 'months',
        regulation: 'COP13 Section 13.2.3 + Manufacturer',
        requirement: 'Annual professional service',
        certificate: 'Service certificate required',
        notes: 'MCA/MED approved or BS/EN 396/399'
      });
    }
    
    // Night operations require lights on lifejackets
    if (vessel.operatesAtNight) {
      safety.push({
        category: MAINTENANCE_CATEGORIES.LSA,
        item: 'Lifejacket Light Check',
        interval: 3,
        unit: 'months',
        regulation: 'COP13 Section 13.2.4',
        requirement: 'Test all lifejacket lights',
        notes: 'Required for night operations'
      });
    }
  }
  
  // COP13 Section 13.1 - Lifebuoys
  // Not needed if all passengers/crew wear lifejackets (13.1.4)
  if (!vessel.allPersonsWearLifejackets && vessel.length >= 10) {
    safety.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Lifebuoy Inspection',
      interval: 6,
      unit: 'months',
      regulation: 'COP13 Section 13.1',
      requirement: 'Check condition, grab lines, markings',
      notes: 'Minimum 1 required for vessels >10m'
    });
  }
  
  // COP13 Section 13.3 - Liferafts (Category D waters only)
  if (waterCategory === 'D' && vessel.carriesPassengers) {
    safety.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Liferaft Annual Service',
      interval: 12,
      unit: 'months',
      regulation: 'COP13 Section 13.3',
      requirement: 'Annual service at approved station',
      certificate: 'Service certificate required',
      notes: 'DfT approved or ISAF OSR type'
    });
  }
  
  // Vessel type specific requirements for inland waters
  if (vessel.type === 'pax') {
    // Passenger vessels have additional PA requirements
    safety.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Public Address System Check',
      interval: 3,
      unit: 'months',
      regulation: 'Local requirements',
      requirement: 'Test PA system for passenger announcements',
      notes: 'Required for passenger operations'
    });
  }
  
  if (vessel.type === 'tug' || vessel.operations?.includes('towing_operations')) {
    // Towing vessels in inland waters need enhanced safety
    safety.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Towing Safety Equipment Check',
      interval: 1,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Check towing equipment and emergency release',
      notes: 'Enhanced safety for towing operations'
    });
  }
  
  if (vessel.type === 'pilot') {
    // Pilot boats need specialized transfer equipment
    safety.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Pilot Transfer Equipment Check',
      interval: 1,
      unit: 'months',
      regulation: 'Local requirements',
      requirement: 'Check pilot ladder, boarding arrangements, safety equipment',
      notes: 'Safe pilot transfer arrangements'
    });
  }
  
  if (vessel.type === 'survey' || vessel.operations?.includes('survey_operations')) {
    // Survey vessels need enhanced safety protocols
    safety.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Survey Equipment Safety Check',
      interval: 1,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Check survey equipment safety, man overboard procedures',
      notes: 'Protection for survey operations'
    });
  }
  
  if (vessel.type === 'police' || vessel.type === 'patrol') {
    // Police/patrol vessels need enhanced emergency equipment
    safety.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Emergency Response Equipment Check',
      interval: 1,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Check emergency response and rescue equipment',
      notes: 'Enhanced equipment for emergency response duties'
    });
  }
  
  if (vessel.type === 'ctv' || vessel.operations?.includes('crew_transfer')) {
    // Crew transfer vessels need safe transfer arrangements
    safety.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Crew Transfer Safety Check',
      interval: 1,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Check transfer arrangements, safety barriers, emergency procedures',
      notes: 'Safe crew transfer operations'
    });
  }
  
  if (vessel.type === 'cargo' || vessel.operations?.includes('cargo_operations')) {
    // Cargo vessels need enhanced deck safety
    safety.push({
      category: MAINTENANCE_CATEGORIES.LSA,
      item: 'Cargo Handling Safety Check',
      interval: 1,
      unit: 'months',
      regulation: 'Good practice',
      requirement: 'Check deck safety, cargo securing equipment, emergency procedures',
      notes: 'Safe cargo handling operations'
    });
  }

  // COP13 Section 13.5 - All LSA maintenance per manufacturer
  safety.push({
    category: MAINTENANCE_CATEGORIES.LSA,
    item: 'LSA Instruction Manual Review',
    interval: 12,
    unit: 'months',
    regulation: 'COP13 Section 13.4.1',
    requirement: 'Review maintenance manual and checklists',
    notes: 'Update maintenance schedule and spare parts list'
  });
  
  return safety;
}

// INLAND WATERWAY NAVIGATION REQUIREMENTS
function getInlandNavigationMaintenance(vessel) {
  const nav = [];
  
  // Basic navigation lights - still required
  nav.push({
    category: MAINTENANCE_CATEGORIES.NAVIGATION,
    item: 'Navigation Lights Test',
    interval: 6,
    unit: 'months',
    regulation: 'CEVNI/Local Rules',
    requirement: 'Test all lights and switches',
    notes: 'Inland waterway light configurations'
  });
  
  // Sound signals - still required
  nav.push({
    category: MAINTENANCE_CATEGORIES.NAVIGATION,
    item: 'Sound Signal Test',
    interval: 12,
    unit: 'months',
    regulation: 'CEVNI/Local Rules',
    requirement: 'Test horn/whistle',
    notes: 'Required for navigation'
  });
  
  // VHF Radio - usually required
  nav.push({
    category: MAINTENANCE_CATEGORIES.RADIO,
    item: 'VHF Radio Check',
    interval: 12,
    unit: 'months',
    regulation: 'Inland Waterways Code',
    requirement: 'Basic function test',
    notes: 'No DSC required for inland'
  });
  
  // AIS if required locally
  if (vessel.tonnage >= 300 || vessel.carriesPassengers) {
    nav.push({
      category: MAINTENANCE_CATEGORIES.NAVIGATION,
      item: 'Inland AIS Check',
      interval: 12,
      unit: 'months',
      regulation: 'Local requirements',
      requirement: 'Verify data transmission',
      notes: 'May be required on busy waterways'
    });
  }
  
  return nav;
}

// INLAND WATERWAY MEDICAL REQUIREMENTS
function getInlandMedicalRequirements(vessel) {
  const medical = [];
  
  // Basic first aid kit
  medical.push({
    category: MAINTENANCE_CATEGORIES.MEDICAL,
    item: 'First Aid Kit Check',
    interval: 6,
    unit: 'months',
    regulation: 'Inland Waterways Code',
    requirement: 'Check contents and expiry dates',
    notes: 'Basic kit sufficient for inland waters'
  });
  
  // No enhanced medical stores typically required
  
  return medical;
}

// INLAND WATERWAY FFE REQUIREMENTS - Based on COP13 (Inland Waters Small Passenger Boat Code)
function getInlandFFEMaintenance(vessel) {
  const ffe = [];
  
  // Determine inland water category (A, B, C, or D)  
  const waterCategory = vessel.waterCategory || vessel.inlandWaterCategory || 'C';
  
  // Section 15.1 - No fire equipment needed for non-decked vessels without engines/fuel
  const hasEngine = vessel.engineType !== 'none';
  const hasFuelBurning = vessel.hasGalley || vessel.hasHeating || hasEngine;
  
  if (!hasFuelBurning && !vessel.hasDecking) {
    // No fire equipment required per COP13 15.1
    return ffe;
  }
  
  // Section 15.2 - Fixed fire system for INBOARD engine spaces only
  // Must be remotely operated from outside the space
  if (vessel.engineType && vessel.engineType !== 'outboard' && vessel.engineType !== 'none') {
    ffe.push({
      category: MAINTENANCE_CATEGORIES.FFE,
      item: 'Fixed Fire System (Engine Space)',
      interval: 12,
      unit: 'months',
      regulation: 'COP13 Section 15.2',
      requirement: 'Annual test of remote operation',
      notes: 'Min rating 5A/34B, operable from outside space',
      jobDescription: {
        purpose: 'Verify fixed fire suppression system will activate in emergency',
        scope: `• Check system pressure gauge is in green zone
• Inspect nozzles for blockage or damage
• Verify remote pull cables move freely
• Test manual release mechanism (without discharge)
• Check engine compartment seals are intact
• Verify warning alarms function correctly
• Inspect cylinder mounting and security
• Check discharge indicators are clear
• Test ventilation shut-down interlocks`,
        safety: [
          'Do NOT discharge system during test',
          'Ensure area is well ventilated',
          'Have backup fire extinguisher ready'
        ],
        completion: 'Record pressure reading, test results, and next service date'
      }
    });
  }
  
  // Section 15.3 - Category A waters - outboard vessels need extinguisher
  if (waterCategory === 'A' && vessel.engineType === 'outboard') {
    ffe.push({
      category: MAINTENANCE_CATEGORIES.FFE,
      item: 'Portable Fire Extinguisher',
      interval: 12,
      unit: 'months',
      regulation: 'COP13 Section 15.3',
      requirement: 'Annual service per BS EN 3',
      notes: 'Required for outboard vessels in Category A waters'
    });
  }
  
  // Sections 15.4 & 15.5 - Category B, C, D waters - vessels >6m
  if ((waterCategory === 'B' || waterCategory === 'C' || waterCategory === 'D') && vessel.length > 6) {
    ffe.push({
      category: MAINTENANCE_CATEGORIES.FFE,
      item: 'Fire Fighting Equipment',
      interval: 6,
      unit: 'months',
      regulation: `COP13 Section 15.${waterCategory === 'B' ? '4' : '5'}`,
      requirement: 'Check pump/extinguisher (13A/113B min)',
      notes: 'Fire pump OR extinguisher required'
    });
    
    ffe.push({
      category: MAINTENANCE_CATEGORIES.FFE,
      item: 'Fire Buckets',
      interval: 3,
      unit: 'months',
      regulation: `COP13 Section 15.${waterCategory === 'B' ? '4' : '5'}`,
      requirement: 'Check buckets and lanyards',
      notes: 'Metal, plastic or canvas acceptable'
    });
  }
  
  // Section 15.6 - Accommodation space extinguishers (all vessels except 15.1)
  if (hasFuelBurning && vessel.hasAccommodation) {
    ffe.push({
      category: MAINTENANCE_CATEGORIES.FFE,
      item: 'Accommodation Extinguishers',
      interval: 12,
      unit: 'months',
      regulation: 'COP13 Section 15.6',
      requirement: 'Service extinguishers at each exit',
      notes: 'Min 5A/34B at each exit, minimum 2 total'
    });
  }
  
  // Section 15.7 - Fire blanket for galley
  if (vessel.hasGalley) {
    ffe.push({
      category: MAINTENANCE_CATEGORIES.FFE,
      item: 'Fire Blanket',
      interval: 12,
      unit: 'months',
      regulation: 'COP13 Section 15.7',
      requirement: 'Check condition and mounting',
      notes: 'Located between door and stove'
    });
  }
  
  // CO detector if accommodation
  if (vessel.hasAccommodation && vessel.hasGasAppliances) {
    ffe.push({
      category: MAINTENANCE_CATEGORIES.FFE,
      item: 'CO Detector Test',
      interval: 1,
      unit: 'months',
      regulation: 'Gas Safety Regulations',
      requirement: 'Test button check'
    });
  }
  
  return ffe;
}

// INLAND WATERWAY ELECTRICAL REQUIREMENTS - NO WBC3 REFERENCES
function getInlandElectricalMaintenance(vessel) {
  const electrical = [];
  
  electrical.push({
    category: MAINTENANCE_CATEGORIES.ELECTRICAL,
    item: 'Battery Maintenance',
    interval: 1,
    unit: 'months',
    regulation: 'Electrical Standards',
    requirement: 'Check levels and terminals'
  });
  
  electrical.push({
    category: MAINTENANCE_CATEGORIES.ELECTRICAL,
    item: 'Electrical Safety Test',
    interval: 12,
    unit: 'months',
    regulation: 'IEC 60092',
    requirement: 'Insulation resistance test'
  });
  
  if (vessel.hasShorepower) {
    electrical.push({
      category: MAINTENANCE_CATEGORIES.ELECTRICAL,
      item: 'Shore Power Safety',
      interval: 'Before use',
      regulation: 'Electrical Safety',
      requirement: 'RCD test'
    });
  }
  
  electrical.push({
    category: MAINTENANCE_CATEGORIES.ELECTRICAL,
    item: 'Emergency Lighting',
    interval: 3,
    unit: 'months',
    regulation: 'Safety Standards',
    requirement: 'Function test'
  });
  
  return electrical;
}

// INLAND WATERWAY MACHINERY MAINTENANCE - NO WBC3 REFERENCES
function getInlandMachineryMaintenance(vessel) {
  const machinery = [];
  const engineType = vessel.engineType || 'outboard'; // RIBs typically have outboards
  
  // Basic engine maintenance - manufacturer specs only
  machinery.push({
    category: MAINTENANCE_CATEGORIES.MACHINERY,
    item: 'Engine Service',
    interval: engineType === 'outboard' ? 100 : 250,
    unit: 'hours',
    regulation: 'Manufacturer Specifications',
    requirement: 'Oil, filters, spark plugs as required',
    alternative: 'Annual if low hours'
  });
  
  // Fuel system check
  machinery.push({
    category: MAINTENANCE_CATEGORIES.MACHINERY,
    item: 'Fuel System Check',
    interval: 12,
    unit: 'months',
    regulation: 'Good Practice',
    requirement: 'Check for leaks, filter condition',
    notes: 'Visual inspection'
  });
  
  // For outboards (common on RIBs)
  if (engineType === 'outboard') {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Outboard Lower Unit',
      interval: 12,
      unit: 'months',
      regulation: 'Manufacturer',
      requirement: 'Gear oil change',
      notes: 'Check water pump impeller'
    });
    
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Propeller Inspection',
      interval: 3,
      unit: 'months',
      regulation: 'Good Practice',
      requirement: 'Check for damage',
      notes: 'Remove fishing line/debris'
    });
  }
  
  // Steering - simplified for inland
  machinery.push({
    category: MAINTENANCE_CATEGORIES.MACHINERY,
    item: 'Steering Check',
    interval: 6,
    unit: 'months',
    regulation: 'Safety Standards',
    requirement: 'Check operation and free movement',
    notes: 'Lubricate as required'
  });
  
  // Bilge pumps - if fitted (many small RIBs don't have them)
  if (vessel.hasBilgePump) {
    machinery.push({
      category: MAINTENANCE_CATEGORIES.MACHINERY,
      item: 'Bilge Pump Test',
      interval: 3,
      unit: 'months',
      regulation: 'Good Practice',
      requirement: 'Test operation',
      notes: 'Manual or electric'
    });
  }
  
  return machinery;
}
// CLASS IX(A) REQUIREMENTS FOR SELF-DRIVE HIRE BOATS
// MGN 469 Section 14.2 - Full merchant shipping regulations
function getClassIXARequirements(vessel) {
  const requirements = [];
  
  // Life Saving Appliances - SI 2721/1999 + MSN 1676
  requirements.push({
    category: MAINTENANCE_CATEGORIES.LSA,
    item: 'Lifebuoys (Class IX(A) Standard)',
    interval: 6,
    unit: 'months',
    regulation: 'SI 2721/1999 + MSN 1676',
    requirement: 'Minimum 2 for <24m, 4 for ≥24m vessels',
    notes: 'One by wheelhouse, buoyant lines, lights as per standards'
  });
  
  requirements.push({
    category: MAINTENANCE_CATEGORIES.LSA,
    item: 'Lifejackets (Class IX(A) Standard)',
    interval: 12,
    unit: 'months',
    regulation: 'SI 2721/1999 + MSN 1676',
    requirement: 'Annual inspection to merchant shipping standards',
    notes: '100% of maximum persons + 10% spare'
  });
  
  // Fire Fighting Equipment - SI 1011/1998 + MSN 1665
  requirements.push({
    category: MAINTENANCE_CATEGORIES.FFE,
    item: 'Fire Extinguishers (Class IX(A) Standard)',
    interval: 12,
    unit: 'months',
    regulation: 'SI 1011/1998 + MSN 1665',
    requirement: 'Annual service to merchant shipping standards',
    notes: 'Number and type as per vessel size and risk'
  });
  
  // Additional Class IX(A) requirements
  requirements.push({
    category: MAINTENANCE_CATEGORIES.SURVEYS,
    item: 'MCA Inspection Readiness',
    interval: 3,
    unit: 'months',
    regulation: 'MGN 469 Section 6.1',
    requirement: 'Maintain readiness for MCA surveyor inspection',
    notes: 'MCA has powers to inspect and prevent operation'
  });
  
  requirements.push({
    category: MAINTENANCE_CATEGORIES.LSA,
    item: 'Class IX(A) Documentation Review',
    interval: 6,
    unit: 'months',
    regulation: 'SI 2721/1999',
    requirement: 'Review compliance with all applicable regulations',
    notes: 'Higher regulatory burden than Hire Boat Code'
  });
  
  return requirements;
}

// HIRE BOAT CODE REQUIREMENTS (AINA) - PLACEHOLDER
// MGN 469 Section 14.2 - General Exemption route (recommended)
// TODO: Download and implement actual AINA Hire Boat Code 2021
// Document URL: https://www.aina.org.uk/wp-content/uploads/2021/05/AINA_Hire_Boat_Code_2021.pdf
function getAINAHireBoatCodeRequirements(vessel) {
  const requirements = [];
  
  // AINA Hire Boat Code 2021 - Appendix 1 Maintenance Requirements
  
  // 1. TURN-ROUND CHECKS (Every hire period)
  requirements.push({
    category: MAINTENANCE_CATEGORIES.LSA,
    item: "Turn-round Safety Check",
    interval: 0, // Every hire period
    unit: "hire",
    regulation: "AINA Hire Boat Code 2021 Appendix 1.2",
    requirement: "Lifebuoys, fire extinguishers/blankets, safety rails, life jackets/buoyancy aids",
    mandatory: true,
    checklist: [
      "Check lifebuoys present and secure",
      "Verify fire extinguishers/blankets in place",
      "Test smoke/CO alarms",
      "Count and check life jackets/buoyancy aids",
      "Inspect safety/hand rails",
      "Check ropes, fenders and anchors"
    ]
  });
  
  requirements.push({
    category: MAINTENANCE_CATEGORIES.MACHINERY,
    item: "Turn-round Engine Check",
    interval: 0, // Every hire period
    unit: "hire",
    regulation: "AINA Hire Boat Code 2021 Appendix 1.2",
    requirement: "Fuel levels, engine oil levels, drip trays",
    mandatory: true
  });
  
  // 2. PERIODIC CHECKS (Regular during season)
  requirements.push({
    category: MAINTENANCE_CATEGORIES.LSA,
    item: "Periodic Safety Equipment Check",
    interval: 1,
    unit: "weeks",
    regulation: "AINA Hire Boat Code 2021 Appendix 1.3",
    requirement: "Slip-resistant surfaces, safety signs, labels",
    checklist: [
      "Check slip-resistant surfaces condition",
      "Verify safety signs visible (e.g., limited access areas)",
      "Check labels clear (e.g., throttle/gear control position)",
      "Inspect sails and awnings (if applicable)",
      "Check weed hatch security"
    ]
  });
  
  requirements.push({
    category: MAINTENANCE_CATEGORIES.MACHINERY,
    item: "Periodic Machinery Check",
    interval: 1,
    unit: "weeks",
    regulation: "AINA Hire Boat Code 2021 Appendix 1.3",
    requirement: "Bilge pumping systems, cables, controls, switches, instruments",
    checklist: [
      "Test bilge pumping systems",
      "Check cables and controls",
      "Test switches and instruments",
      "Verify battery master switches",
      "Check alternator drive belt",
      "Check coolant level",
      "Verify hydraulic system levels",
      "Visual check ventilation clear"
    ]
  });
  
  requirements.push({
    category: MAINTENANCE_CATEGORIES.ELECTRICAL,
    item: "Periodic Electrical Check",
    interval: 1,
    unit: "weeks",
    regulation: "AINA Hire Boat Code 2021 Appendix 1.3",
    requirement: "Shore power RCD, battery security, cables",
    checklist: [
      "Test shore power RCD",
      "Check battery security and terminals covered",
      "Inspect electric cables condition",
      "Check bilge water levels"
    ]
  });
  
  requirements.push({
    category: MAINTENANCE_CATEGORIES.FFE,
    item: "Periodic Fuel/Gas System Check",
    interval: 1,
    unit: "weeks",
    regulation: "AINA Hire Boat Code 2021 Appendix 1.3",
    requirement: "Fuel and gas hoses condition",
    mandatory: true
  });
  
  // 3. MAJOR SERVICING (Annual)
  requirements.push({
    category: MAINTENANCE_CATEGORIES.MACHINERY,
    item: "Annual Engine/Gearbox Service",
    interval: 12,
    unit: "months",
    regulation: "AINA Hire Boat Code 2021 Appendix 1.5",
    requirement: "Full engine/gearbox service per manufacturer recommendations",
    mandatory: true,
    checklist: [
      "Oil/filter changes",
      "Valve clearances",
      "Drive belt condition",
      "Cabling and terminals",
      "Starters",
      "Leak checking",
      "Mountings condition and alignment",
      "Exhaust serviceability and lagging"
    ]
  });
  
  requirements.push({
    category: MAINTENANCE_CATEGORIES.SURVEYS,
    item: "Annual Hull Integrity Inspection",
    interval: 12,
    unit: "months",
    regulation: "AINA Hire Boat Code 2021 Appendix 1.5",
    requirement: "Hull integrity, sea cocks, through hull connections, weed hatch",
    mandatory: true
  });
  
  requirements.push({
    category: MAINTENANCE_CATEGORIES.MACHINERY,
    item: "Annual Cooling System Service",
    interval: 12,
    unit: "months",
    regulation: "AINA Hire Boat Code 2021 Appendix 1.5",
    requirement: "Flush, check hoses/clips, leak test, includes calorifiers"
  });
  
  requirements.push({
    category: MAINTENANCE_CATEGORIES.MACHINERY,
    item: "Annual Fuel System Inspection",
    interval: 12,
    unit: "months",
    regulation: "AINA Hire Boat Code 2021 Appendix 1.5",
    requirement: "Pipes, connections, tanks condition check - must be leak-free",
    mandatory: true
  });
  
  requirements.push({
    category: MAINTENANCE_CATEGORIES.MACHINERY,
    item: "Annual Stern-gear Service",
    interval: 12,
    unit: "months",
    regulation: "AINA Hire Boat Code 2021 Appendix 1.5",
    requirement: "Inspect and service stern-gear, cables and controls"
  });
  
  requirements.push({
    category: MAINTENANCE_CATEGORIES.FFE,
    item: "Annual Fire Extinguisher Inspection",
    interval: 12,
    unit: "months",
    regulation: "AINA Hire Boat Code 2021 Appendix 1.5",
    requirement: "Annual inspection of all fire extinguishers",
    mandatory: true
  });
  
  // Gas Safety - Most critical for hire boats
  if (vessel.hasGasSystem) {
    requirements.push({
      category: MAINTENANCE_CATEGORIES.FFE,
      item: "Annual Gas Safety (GSIUR) Inspection",
      interval: 12,
      unit: "months",
      regulation: "AINA Hire Boat Code 2021 Appendix 1.5 / Gas Safety (Installation and Use) Regulations",
      requirement: "All joints, flexible pipes, regulators, burners, flame failure devices",
      mandatory: true,
      authority: "Gas Safe Registered Engineer",
      notes: "Legal requirement for all hire boats with gas systems"
    });
  }
  
  requirements.push({
    category: MAINTENANCE_CATEGORIES.ELECTRICAL,
    item: "Annual Battery Service",
    interval: 12,
    unit: "months",
    regulation: "AINA Hire Boat Code 2021 Appendix 1.5",
    requirement: "Battery condition, cables and terminals"
  });
  
  requirements.push({
    category: MAINTENANCE_CATEGORIES.ELECTRICAL,
    item: "Annual Electrical Inspection",
    interval: 12,
    unit: "months",
    regulation: "AINA Hire Boat Code 2021 Appendix 1.5",
    requirement: "Condition of visible cables and terminals"
  });
  
  // AINA Code specific compliance requirements
  requirements.push({
    category: MAINTENANCE_CATEGORIES.SURVEYS,
    item: "Stability Compliance Declaration",
    interval: 0, // One-time or after major modifications
    unit: "initial",
    regulation: "AINA Hire Boat Code 2021 Section 3.2.3 & Appendix III",
    requirement: "Valid stability compliance declaration required",
    mandatory: true,
    notes: "Required on entry to hire service or after significant alterations"
  });
  
  // Handover audit - recommended in the code
  requirements.push({
    category: MAINTENANCE_CATEGORIES.SURVEYS,
    item: "Handover Audit",
    interval: 12,
    unit: "months",
    regulation: "AINA Hire Boat Code 2021 Section 3.3.7",
    requirement: "Independent audit of handover arrangements",
    notes: "Strongly recommended - may be required by licensing authority"
  });
  
  // Risk Assessment requirement
  requirements.push({
    category: MAINTENANCE_CATEGORIES.SURVEYS,
    item: "Risk Assessment Review",
    interval: 12,
    unit: "months",
    regulation: "AINA Hire Boat Code 2021 Section 2.6",
    requirement: "Review and update risk assessments for changed circumstances",
    mandatory: true,
    notes: "Must consider incidents, hirer profile, boat modifications, environmental changes"
  });
  
  return requirements;
}